import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { DuotoneImage } from '@/components/site/DuotoneImage';
import { SITE, SERVICES } from '@/data/site';
import { HERO_IMAGES } from '@/data/heroImages';

gsap.registerPlugin(ScrollTrigger);

export default function Services() {
  const indexRef = useRef(null);

  // signature animation: horizontal pin reveal of service deliverables marquee row
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !indexRef.current) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.svc-row').forEach((row) => {
        gsap.fromTo(
          row.querySelector('.svc-rule'),
          { scaleX: 0 },
          {
            scaleX: 1,
            ease: 'power2.out',
            scrollTrigger: { trigger: row, start: 'top 80%', end: 'top 40%', scrub: 0.5 },
          }
        );
      });
    }, indexRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.28} />
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-40 pb-20 md:pb-28">
          <SectionLabel as="h1" num="01" data-testid="services-eyebrow">
            Web, App &amp; AI Development Services — Pune · Mumbai · Nashik
          </SectionLabel>
          <WordReveal
            as="p"
            text="We build the products founders are too close to see clearly."
            accent="clearly"
            data-testid="services-headline"
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[80px] font-extrabold tracking-tightest leading-[1.02] text-balance max-w-5xl"
          />
          <Reveal delay={500}>
            <p className="mt-10 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed">
              Pick the engagement; we’ll bring the people. Website development, web and mobile apps,
              custom software, ecommerce and AI — each with a defined week-zero brief, a measurable
              launch, and a quiet post-launch month.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INDEX LIST — line by line, intentional asymmetry */}
      <section ref={indexRef} className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-20">
          {SERVICES.map((s, i) => (
            <article
              key={s.slug}
              id={s.slug}
              data-testid={`service-${s.slug}`}
              className="svc-row group relative scroll-mt-32 border-t border-white/10 first:border-t-0 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8"
            >
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">
                  {s.num} / 06
                </div>
                <div className="svc-rule mt-4 h-px w-24 bg-gradient-to-r from-violet to-electric origin-left" />
              </div>

              <div className="md:col-span-6">
                <Reveal>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.05] transition-colors duration-500 group-hover:text-white">
                    {s.title}
                  </h2>
                </Reveal>
                <Reveal delay={120}>
                  <p className="mt-5 text-ink-muted leading-relaxed max-w-xl">{s.long}</p>
                </Reveal>
                <Reveal delay={180}>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {s.deliverables.map((d) => (
                      <span
                        key={d}
                        className="text-xs px-3 py-1.5 rounded-full border border-white/[0.12] text-white/85"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>

              <div className="md:col-span-4 md:pl-8 md:border-l border-white/10 flex flex-col justify-between gap-8">
                {HERO_IMAGES[s.slug] && (
                  <Reveal delay={200}>
                    <Link
                      to={`/services/${s.slug}/`}
                      tabIndex={-1}
                      aria-hidden
                      className="relative block aspect-[16/10] overflow-hidden rounded-[16px] border border-white/[0.12] transition-colors duration-500 group-hover:border-white/25"
                    >
                      <DuotoneImage src={HERO_IMAGES[s.slug].src} alt={HERO_IMAGES[s.slug].alt} duotone={0.4} />
                    </Link>
                  </Reveal>
                )}
                <Reveal delay={240}>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">
                      Engagement
                    </div>
                    <div className="mt-3 text-lg text-white/90 leading-relaxed">
                      {['6–10 wks', '8–14 wks', '10–16 wks', '8–14 wks', '10–16 wks', '6–12 wks'][i] || '8–12 wks'}
                      <span className="text-ink-muted"> · fixed scope or retainer</span>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={300}>
                  <div className="flex flex-col gap-3 items-start">
                    <Link
                      to={`/services/${s.slug}/`}
                      data-testid={`service-detail-${s.slug}`}
                      className="arrow-link inline-flex items-center gap-2 text-sm text-violet"
                    >
                      Read the full page <span className="arrow">→</span>
                    </Link>
                    <a
                      href={SITE.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-cta-${s.slug}`}
                      className="arrow-link inline-flex items-center gap-2 text-sm text-white border-b border-white/30 pb-1 hover:border-white"
                    >
                      Start with this <ArrowUpRight size={14} className="arrow" />
                    </a>
                  </div>
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="02">How we engage</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Three ways to <span className="italic text-violet">work with us.</span>
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                title: 'Sprint',
                price: '₹3–6L',
                length: '2–4 weeks',
                blurb: 'A focused discovery + prototype sprint. Best for new ideas with fuzzy edges.',
              },
              {
                title: 'Build',
                price: '₹12–35L',
                length: '8–16 weeks',
                blurb: 'A complete design + engineering engagement to ship v1 of a product or site.',
              },
              {
                title: 'Partner',
                price: '₹6L+ / mo',
                length: '6 months+',
                blurb: 'Fractional product team for founders who need an in-house gear box without the hires.',
              },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <div
                  data-testid={`engagement-${p.title.toLowerCase()}`}
                  className="group h-full rounded-[20px] border border-white/[0.12] bg-ink-bg p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_50px_-20px_rgba(108,92,231,0.45)]"
                >
                  <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">
                    {p.length}
                  </div>
                  <div className="mt-4 text-3xl font-bold tracking-tight">{p.title}</div>
                  <div className="mt-1 text-violet font-medium tabular-nums">{p.price}</div>
                  <p className="mt-5 text-sm text-ink-muted leading-relaxed">{p.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Pick one, or start with a conversation"
        headline="Tell us what you’re building."
        sub="We reply within a working day. If you’re a good fit we’ll say so. If not, we’ll introduce you to someone who is."
      />
    </div>
  );
}
